import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, Trash2, Download, FileText, Users, Plus, Edit, Save, X, DollarSign, Package, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const ContentManagement = () => {
  const [promos, setPromos] = useState([]);
  const [users, setUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    item_no: '',
    item_designation: '',
    description: '',
    special_net_price: '',
    slsp_net_price: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [promoAssignments, setPromoAssignments] = useState({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPromoForAssign, setSelectedPromoForAssign] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  useEffect(() => {
    fetchPromos();
    fetchUsers();
  }, []);

  const fetchPromos = async () => {
    try {
      const { data, error } = await supabase
        .from('monthly_promos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPromos(data || []);
      
      // Fetch assignments for all promos
      if (data && data.length > 0) {
        await fetchAllAssignments(data.map(p => p.id));
      }
    } catch (error) {
      console.error('Error fetching promos:', error);
      showError(error, 'Failed to fetch promos');
    } finally {
      setLoading(false);
    }
  };

  // Fetch assignments for promos
  const fetchAllAssignments = async (promoIds) => {
    try {
      const { data, error } = await supabase
        .from('promo_assignments')
        .select(`
          promo_id,
          user_id,
          accounts!fk_promo_assignments_user_id (
            id,
            email,
            full_name
          )
        `)
        .in('promo_id', promoIds);

      if (error) throw error;

      // Group assignments by promo_id
      const assignmentsByPromo = {};
      data.forEach(assignment => {
        if (!assignmentsByPromo[assignment.promo_id]) {
          assignmentsByPromo[assignment.promo_id] = [];
        }
        assignmentsByPromo[assignment.promo_id].push({
          user_id: assignment.user_id,
          email: assignment.accounts.email,
          full_name: assignment.accounts.full_name
        });
      });

      setPromoAssignments(assignmentsByPromo);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      showError(new Error('Invalid file type'), 'Please select a JPG or PNG image');
      e.target.value = '';
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      showError(new Error('File too large'), 'Image must be less than 5MB');
      e.target.value = '';
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Clear image selection
  const clearImageSelection = () => {
    setImageFile(null);
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById('promo-image-input');
    if (fileInput) fileInput.value = '';
  };

  // Upload image to Supabase Storage
  const uploadPromoImage = async () => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);
      
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('promos')
        .upload(filePath, imageFile);

      if (uploadError) throw uploadError;

      // Fix: Get public URL correctly for Supabase Storage
      const { data } = supabase.storage
        .from('promos')
        .getPublicUrl(filePath);

      return { path: filePath, url: data.publicUrl };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  // Delete image from Supabase Storage
  const deletePromoImage = async (imagePath) => {
    if (!imagePath) return;

    try {
      const { error } = await supabase.storage
        .from('promos')
        .remove([imagePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting image:', error);
      // Don't throw - allow deletion to continue even if storage cleanup fails
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, email, full_name')
        .order('email', { ascending: true });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showError(error, 'Failed to fetch users');
    }
  };

  const handlePromoUpload = async (event) => {
    try {
      setUploading(true);
      const file = event.target.files[0];
      
      if (!file) return;
      
      if (file.type !== 'application/pdf') {
        showError(new Error('Only PDF files are allowed'), 'Only PDF files are allowed');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      let { error: uploadError } = await supabase.storage
        .from('promos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Fix: Get public URL correctly for Supabase Storage
      const { data: urlData } = supabase.storage
        .from('promos')
        .getPublicUrl(filePath);

      const { data, error: insertError } = await supabase
        .from('monthly_promos')
        .insert({
          file_name: file.name,
          file_path: filePath,
          public_url: urlData.publicUrl,
          uploaded_by: user.id
        })
        .select();

      if (insertError) throw insertError;

      showNotification('Promo PDF uploaded successfully!', 'success');
      fetchPromos();
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      showError(error, 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePromo = async (id, filePath) => {
    if (!window.confirm('Are you sure you want to delete this promo?')) {
      return;
    }

    try {
      const { error: deleteStorageError } = await supabase.storage
        .from('promos')
        .remove([filePath]);

      if (deleteStorageError) throw deleteStorageError;

      const { data: deleteData, error: deleteDbError } = await supabase
        .from('monthly_promos')
        .delete()
        .eq('id', id)
        .select();

      if (deleteDbError) throw deleteDbError;
      if (deleteData.length === 0) throw new Error('No promo was deleted. Check RLS policies.');

      showNotification('Promo deleted successfully', 'success');
      fetchPromos();
    } catch (error) {
      console.error('Error deleting promo:', error);
      showError(error, 'Failed to delete promo');
    }
  };

  // Open assign modal
  const openAssignModal = (promo) => {
    setSelectedPromoForAssign(promo);
    // Pre-select already assigned users
    const assignedUserIds = (promoAssignments[promo.id] || []).map(a => a.user_id);
    setSelectedUserIds(assignedUserIds);
    setShowAssignModal(true);
  };

  // Close assign modal
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedPromoForAssign(null);
    setSelectedUserIds([]);
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Save assignments
  const handleSaveAssignments = async () => {
    if (!selectedPromoForAssign) return;

    try {
      const promoId = selectedPromoForAssign.id;
      const currentAssignments = (promoAssignments[promoId] || []).map(a => a.user_id);

      // Find users to add and remove
      const toAdd = selectedUserIds.filter(id => !currentAssignments.includes(id));
      const toRemove = currentAssignments.filter(id => !selectedUserIds.includes(id));

      // Remove unselected assignments
      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('promo_assignments')
          .delete()
          .eq('promo_id', promoId)
          .in('user_id', toRemove);

        if (deleteError) throw deleteError;
      }

      // Add new assignments
      if (toAdd.length > 0) {
        const { error: insertError } = await supabase
          .from('promo_assignments')
          .insert(toAdd.map(userId => ({ promo_id: promoId, user_id: userId })));

        if (insertError) throw insertError;
      }

      showNotification('Assignments updated successfully', 'success');
      closeAssignModal();
      
      // Refresh assignments
      await fetchAllAssignments(promos.map(p => p.id));
    } catch (error) {
      console.error('Error updating assignments:', error);
      showError(error, 'Failed to update assignments');
    }
  };

  // Remove single assignment
  const handleRemoveAssignment = async (promoId, userId) => {
    try {
      const { error } = await supabase
        .from('promo_assignments')
        .delete()
        .eq('promo_id', promoId)
        .eq('user_id', userId);

      if (error) throw error;

      showNotification('Assignment removed', 'success');
      
      // Refresh assignments
      await fetchAllAssignments(promos.map(p => p.id));
    } catch (error) {
      console.error('Error removing assignment:', error);
      showError(error, 'Failed to remove assignment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.item_no.trim()) {
      errors.item_no = 'Item number is required';
    }
    
    if (!formData.item_designation.trim()) {
      errors.item_designation = 'Item designation is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (!formData.special_net_price || parseFloat(formData.special_net_price) <= 0) {
      errors.special_net_price = 'Special price must be a positive number';
    }
    
    if (!formData.slsp_net_price || parseFloat(formData.slsp_net_price) <= 0) {
      errors.slsp_net_price = 'SLSP price must be a positive number';
    }
    
    // SLSP price must be lower than special price
    if (formData.slsp_net_price && formData.special_net_price) {
      const slspPrice = parseFloat(formData.slsp_net_price);
      const specialPrice = parseFloat(formData.special_net_price);
      if (slspPrice >= specialPrice) {
        errors.slsp_net_price = 'SLSP price must be lower than special price';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      // Build promo data (common fields)
      const promoData = {
        item_no: formData.item_no.trim(),
        item_designation: formData.item_designation.trim(),
        description: formData.description.trim(),
        special_net_price: parseFloat(formData.special_net_price),
        slsp_net_price: parseFloat(formData.slsp_net_price)
      };

      // Only set uploaded_by when creating new promo (not when editing)
      if (!editingId) {
        promoData.uploaded_by = user.id;
      }

      // Upload image if one was selected
      if (imageFile) {
        const imageData = await uploadPromoImage();
        if (imageData) {
          promoData.promo_image_path = imageData.path;
          promoData.promo_image_url = imageData.url;
        }
      }
      
      if (editingId) {
        // Update existing promo
        const { data, error } = await supabase
          .from('monthly_promos')
          .update(promoData)
          .eq('id', editingId)
          .select();
          
        if (error) throw error;
        if (data.length === 0) throw new Error('No promo was updated. Check RLS policies.');
        
        showNotification('Promo updated successfully!', 'success');
        setEditingId(null);
      } else {
        // Create new promo
        const { data, error } = await supabase
          .from('monthly_promos')
          .insert(promoData)
          .select();
          
        if (error) throw error;
        
        showNotification('Promo created successfully!', 'success');
      }
      
      resetForm();
      fetchPromos();
      
    } catch (error) {
      console.error('Error saving promo:', error);
      showError(error, 'Failed to save promo');
    } finally {
      setSaving(false);
    }
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      item_no: '',
      item_designation: '',
      description: '',
      special_net_price: '',
      slsp_net_price: ''
    });
    setFormErrors({});
    setShowForm(false);
    setEditingId(null);
    clearImageSelection();
  };

  // Handle edit promo
  const handleEdit = (promo) => {
    setFormData({
      item_no: promo.item_no || '',
      item_designation: promo.item_designation || '',
      description: promo.description || '',
      special_net_price: promo.special_net_price || '',
      slsp_net_price: promo.slsp_net_price || ''
    });
    setEditingId(promo.id);
    setShowForm(true);
    setFormErrors({});
    // Set image preview if promo has an image
    if (promo.promo_image_url) {
      setImagePreview(promo.promo_image_url);
    }
  };

  // Handle delete promo image
  const handleDeletePromoImage = async (promo) => {
    if (!window.confirm('Are you sure you want to delete this promo image?')) {
      return;
    }

    try {
      // Delete from storage
      if (promo.promo_image_path) {
        await deletePromoImage(promo.promo_image_path);
      }

      // Update database
      const { error } = await supabase
        .from('monthly_promos')
        .update({
          promo_image_path: null,
          promo_image_url: null
        })
        .eq('id', promo.id);

      if (error) throw error;

      showNotification('Promo image deleted successfully', 'success');
      fetchPromos();
    } catch (error) {
      console.error('Error deleting promo image:', error);
      showError(error, 'Failed to delete promo image');
    }
  };

  // Handle delete promo (updated to handle both PDF and data entries)
  const handleDeletePromoData = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo?')) {
      return;
    }

    try {
      const { data: deleteData, error: deleteDbError } = await supabase
        .from('monthly_promos')
        .delete()
        .eq('id', id)
        .select();

      if (deleteDbError) throw deleteDbError;
      if (deleteData.length === 0) throw new Error('No promo was deleted. Check RLS policies.');

      showNotification('Promo deleted successfully', 'success');
      fetchPromos();
    } catch (error) {
      console.error('Error deleting promo:', error);
      showError(error, 'Failed to delete promo');
    }
  };

  // Calculate savings
  const calculateSavings = (slspPrice, specialPrice) => {
    if (!slspPrice || !specialPrice) return null;
    const savings = parseFloat(specialPrice) - parseFloat(slspPrice);
    const percentage = ((savings / parseFloat(specialPrice)) * 100).toFixed(1);
    return { amount: savings, percentage };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Content"
        description="Create promo data entries and upload promotional materials"
      />

      {/* Manual Promo Data Entry Section */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              {editingId ? 'Edit Promo Data' : 'Add New Promo Data'}
            </h3>
            {!showForm && (
              <Button
                variant="primary"
                onClick={() => setShowForm(true)}
                icon={<Plus className="w-4 h-4" />}
              >
                Add Promo Data
              </Button>
            )}
          </div>

          {showForm && (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleFormSubmit}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Item Number */}
                <div>
                  <label htmlFor="item_no" className="block text-sm font-medium text-gray-700 mb-1">
                    Item Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="item_no"
                    name="item_no"
                    value={formData.item_no}
                    onChange={handleFormChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      formErrors.item_no ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter item number"
                  />
                  {formErrors.item_no && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {formErrors.item_no}
                    </p>
                  )}
                </div>

                {/* Item Designation */}
                <div>
                  <label htmlFor="item_designation" className="block text-sm font-medium text-gray-700 mb-1">
                    Item Designation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="item_designation"
                    name="item_designation"
                    value={formData.item_designation}
                    onChange={handleFormChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      formErrors.item_designation ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter item designation"
                  />
                  {formErrors.item_designation && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {formErrors.item_designation}
                    </p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleFormChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    formErrors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter product description"
                />
                {formErrors.description && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {formErrors.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Special Price */}
                <div>
                  <label htmlFor="special_net_price" className="block text-sm font-medium text-gray-700 mb-1">
                    Special Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="special_net_price"
                      name="special_net_price"
                      step="0.01"
                      min="0"
                      value={formData.special_net_price}
                      onChange={handleFormChange}
                      className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        formErrors.special_net_price ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {formErrors.special_net_price && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {formErrors.special_net_price}
                    </p>
                  )}
                </div>

                {/* SLSP Price */}
                <div>
                  <label htmlFor="slsp_net_price" className="block text-sm font-medium text-gray-700 mb-1">
                    SLSP Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="slsp_net_price"
                      name="slsp_net_price"
                      step="0.01"
                      min="0"
                      value={formData.slsp_net_price}
                      onChange={handleFormChange}
                      className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        formErrors.slsp_net_price ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {formErrors.slsp_net_price && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {formErrors.slsp_net_price}
                    </p>
                  )}
                </div>
              </div>

              {/* Savings Preview */}
              {formData.special_net_price && formData.slsp_net_price && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex items-center">
                    <Package className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Calculated Savings
                      </p>
                      {(() => {
                        const savings = calculateSavings(formData.slsp_net_price, formData.special_net_price);
                        return savings ? (
                          <p className="text-lg font-bold text-green-700">
                            ${savings.amount.toFixed(2)} ({savings.percentage}% OFF)
                          </p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Promo Image Upload */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <ImageIcon className="h-5 w-5 text-gray-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">Promo Image (Optional)</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Upload a custom image to display on the SLSP promo page (JPG or PNG, max 5MB)
                </p>

                {imagePreview ? (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Promo preview"
                        className="h-40 w-auto rounded-lg border border-gray-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={clearImageSelection}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        title="Remove image"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 font-medium">
                        Change Image
                        <input
                          id="promo-image-input"
                          type="file"
                          className="hidden"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleImageSelect}
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center px-4 py-6 bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                    <span className="mt-2 text-sm text-gray-600">Click to select image</span>
                    <span className="mt-1 text-xs text-gray-500">JPG or PNG, max 5MB</span>
                    <input
                      id="promo-image-input"
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleImageSelect}
                    />
                  </label>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={resetForm}
                  icon={<X className="w-4 h-4" />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={saving}
                  icon={<Save className="w-4 h-4" />}
                >
                  {saving ? 'Saving...' : (editingId ? 'Update Promo' : 'Create Promo')}
                </Button>
              </div>
            </motion.form>
          )}
        </div>
      </div>

      {/* PDF Upload Section */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Upload New Promo
          </h3>
          <div className="max-w-sm">
            <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-gray-600 rounded-lg tracking-wide border-2 border-dashed border-gray-300 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors">
              <Upload className="w-8 h-8" />
              <span className="mt-2 text-base leading-normal">
                {uploading ? 'Uploading...' : 'Select PDF file'}
              </span>
              <input
                type="file"
                className="hidden"
                onChange={handlePromoUpload}
                disabled={uploading}
                accept=".pdf"
              />
            </label>
            <p className="mt-2 text-sm text-gray-500">
              Only PDF files are allowed. Maximum file size: 10MB
            </p>
          </div>
        </div>
      </div>

      {/* Promos List */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Existing Promos ({promos.length})
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading promos...</span>
            </div>
          ) : promos.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No promos created yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {promos.map((promo) => {
                const hasData = promo.item_no || promo.item_designation || promo.description;
                const hasPDF = promo.file_name || promo.file_path;
                const savings = calculateSavings(promo.slsp_net_price, promo.special_net_price);
                
                return (
                  <motion.div
                    key={promo.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {/* Promo Image Thumbnail */}
                        {promo.promo_image_url ? (
                          <div className="relative group flex-shrink-0">
                            <img
                              src={promo.promo_image_url}
                              alt="Promo"
                              className="h-16 w-16 object-cover rounded-lg border border-gray-300"
                            />
                            <button
                              onClick={() => handleDeletePromoImage(promo)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete image"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-300">
                            <ImageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            {hasData ? (
                              <Package className="h-6 w-6 text-blue-500" />
                            ) : (
                              <FileText className="h-6 w-6 text-red-500" />
                            )}
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {hasData ? promo.item_designation || promo.item_no : promo.file_name}
                                </h4>
                                <div className="flex space-x-1">
                                  {hasData && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      Data
                                    </span>
                                  )}
                                  {hasPDF && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      PDF
                                    </span>
                                  )}
                                  {promo.promo_image_url && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                      Image
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-500">
                                {hasData ? `Item: ${promo.item_no}` : `Uploaded on ${new Date(promo.created_at).toLocaleDateString()}`}
                              </p>
                            </div>
                          </div>
                        
                          {hasData && (
                            <div className="space-y-1">
                            <p className="text-sm text-gray-600">{promo.description}</p>
                            <div className="flex items-center space-x-4 text-sm">
                              {promo.special_net_price && (
                                <span className="text-gray-500">
                                  Special: <span className="line-through">${promo.special_net_price.toFixed(2)}</span>
                                </span>
                              )}
                              {promo.slsp_net_price && (
                                <span className="text-green-600 font-medium">
                                  SLSP: ${promo.slsp_net_price.toFixed(2)}
                                </span>
                              )}
                              {savings && (
                                <span className="text-green-600 font-bold">
                                  Save: ${savings.amount.toFixed(2)} ({savings.percentage}%)
                                </span>
                              )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* Edit Button (only for data entries) */}
                        {hasData && (
                          <button
                            onClick={() => handleEdit(promo)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Assign Users Button */}
                        <button
                          onClick={() => openAssignModal(promo)}
                          className="flex items-center space-x-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                          title="Manage user assignments"
                        >
                          <Users className="h-4 w-4" />
                          <span className="text-xs font-medium">
                            {(promoAssignments[promo.id] || []).length || 'Assign'}
                          </span>
                        </button>

                        {/* Download (only for PDFs) */}
                        {hasPDF && (
                          <a
                            href={promo.public_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-900"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}

                        {/* Delete */}
                        <button
                          onClick={() => hasPDF ? handleDeletePromo(promo.id, promo.file_path) : handleDeletePromoData(promo.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Show assigned users */}
                    {(promoAssignments[promo.id] || []).length > 0 && (
                      <div className="mt-3 ml-20">
                        <p className="text-xs text-gray-500 mb-2">Assigned to:</p>
                        <div className="flex flex-wrap gap-2">
                          {(promoAssignments[promo.id] || []).map((assignment) => (
                            <div
                              key={assignment.user_id}
                              className="inline-flex items-center space-x-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs"
                            >
                              <span>
                                {assignment.full_name || assignment.email}
                              </span>
                              <button
                                onClick={() => handleRemoveAssignment(promo.id, assignment.user_id)}
                                className="text-gray-500 hover:text-red-600 transition-colors"
                                title="Remove assignment"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Multi-User Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Assign Users to Promo</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedPromoForAssign?.item_designation || selectedPromoForAssign?.file_name}
                </p>
              </div>
              <button
                onClick={closeAssignModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                Select the users who should have access to this promo:
              </p>
              
              <div className="space-y-2">
                {users.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {user.full_name || user.email}
                      </p>
                      {user.full_name && (
                        <p className="text-xs text-gray-500">{user.email}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={closeAssignModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAssignments}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Assignments
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Statistics */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Statistics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-gray-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Promos</p>
                  <p className="text-2xl font-semibold text-gray-900">{promos.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex items-center">
                <Upload className="h-8 w-8 text-gray-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">This Month</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {promos.filter(promo => {
                      const promoDate = new Date(promo.created_at);
                      const now = new Date();
                      return promoDate.getMonth() === now.getMonth() && 
                             promoDate.getFullYear() === now.getFullYear();
                    }).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-gray-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentManagement;
